import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaChartLine, FaUserFriends } from 'react-icons/fa';
import { buildUnitDemand } from './propertyRecordUtils';

const VIZ_STATUS_LABEL = {
  available: 'AVAILABLE',
  occupied: 'OCCUPIED',
  highDemand: 'HIGH DEMAND',
};

const PRIORITY_MARK = {
  Hot: '🔥',
  High: '🟡',
};

function openLeadDetail(leadId) {
  if (!leadId) {
    return;
  }
  window.open(`/view/lead/${leadId}`, '_blank', 'noopener,noreferrer');
}

function subtitleForUnit(unit) {
  return [unit.floorLabel, unit.unitType, unit.areaLabel].filter(Boolean).join(' | ');
}

function UnitDetailPanel({ unit }) {
  const [showAllLeads, setShowAllLeads] = useState(false);

  const demand = useMemo(() => buildUnitDemand(unit, unit?.interests), [unit]);

  if (!unit) {
    return (
      <div className='re-unit-panel re-unit-panel--empty'>
        <h3>Unit detail</h3>
        <p>Select a unit on the building to see demand vs price and hot leads.</p>
      </div>
    );
  }

  const visibleLeads = showAllLeads ? demand.hotLeads : demand.hotLeads.slice(0, 3);

  return (
    <aside className='re-unit-panel'>
      <div className='re-unit-panel__head'>
        <h2 className='re-unit-panel__title'>{unit.name || `Unit ${unit.unitNumber}`}</h2>
        <p className='re-unit-panel__subtitle'>{subtitleForUnit(unit)}</p>
      </div>

      <dl className='re-unit-panel__facts'>
        <div>
          <dt>Security Deposit</dt>
          <dd>{unit.depositDisplay}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`re-unit-status re-unit-status--${unit.vizStatus}`}>
              {VIZ_STATUS_LABEL[unit.vizStatus] || unit.status || '—'}
            </span>
          </dd>
        </div>
      </dl>

      <section className='re-unit-panel__section re-demand-vs-price'>
        <h3>
          <FaChartLine aria-hidden='true' /> Demand vs price
        </h3>

        <div className='re-list-rent'>
          <span className='re-list-rent__label'>List rent</span>
          <span className='re-list-rent__value'>{unit.rentDisplay}</span>
        </div>

        <p className='re-unit-panel__leads'>
          <FaUserFriends aria-hidden='true' /> {demand.leadCount} lead
          {demand.leadCount === 1 ? '' : 's'} interested
          {demand.leadCount > 0 && (
            <span className='re-unit-panel__leads-meta'>
              · {demand.atOrAboveRent} at/above rent
            </span>
          )}
        </p>

        <h4 className='re-unit-panel__h4'>Customer budget</h4>
        <div className='re-budget-bars'>
          {demand.distribution.map(band => (
            <div
              key={band.label}
              className={`re-budget-bar${band.containsRent ? ' re-budget-bar--rent' : ''}`}
            >
              <span className='re-budget-bar__label'>
                {band.label}
                {band.containsRent && (
                  <span className='re-budget-bar__rent-tag'>List rent</span>
                )}
              </span>
              <div className='re-budget-bar__track'>
                <div
                  className={`re-budget-bar__fill${band.containsRent ? ' re-budget-bar__fill--rent' : ''}`}
                  style={{ width: `${band.count === 0 ? 4 : band.percent}%` }}
                />
              </div>
              <span className='re-budget-bar__count'>{band.count}</span>
            </div>
          ))}
        </div>

        <div className={`re-pricing-insight re-pricing-insight--${demand.insight.signal}`}>
          {demand.insight.message}
        </div>
      </section>

      <section className='re-unit-panel__section'>
        <h3>Hot leads</h3>
        {visibleLeads.length === 0 ? (
          <p className='re-unit-panel__empty-leads'>No open interested leads on this unit.</p>
        ) : (
          <ul className='re-hot-leads'>
            {visibleLeads.map(lead => (
              <li key={lead.id}>
                {lead.leadId ? (
                  <button
                    type='button'
                    className='re-hot-leads__name re-hot-leads__name--link'
                    onClick={() => openLeadDetail(lead.leadId)}
                    title='Open lead record'
                  >
                    {lead.leadName}
                  </button>
                ) : (
                  <span className='re-hot-leads__name'>{lead.leadName}</span>
                )}
                <span className='re-hot-leads__budget'>{lead.budgetLabel}</span>
                <span className='re-hot-leads__mark' aria-label={lead.priority || 'lead'}>
                  {PRIORITY_MARK[lead.priority] || ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {demand.hotLeads.length > 3 && (
          <Button
            variant='outline-primary'
            size='sm'
            className='re-unit-panel__all'
            onClick={() => setShowAllLeads(open => !open)}
          >
            {showAllLeads ? 'Show top leads' : 'View All Leads'}
          </Button>
        )}
      </section>
    </aside>
  );
}

export default UnitDetailPanel;
